import type { WidgetResponse } from '@content-scheduler/shared/render';
import { peekInstance, type WidgetEnv } from './wix';

type Ready = WidgetEnv & { instance: string; compId: string };

async function call<T>(
    env: Ready,
    method: 'GET' | 'POST',
    path: string,
    body?: object,
): Promise<T> {
    const res = await fetch(path, {
        method,
        headers: {
            Authorization: env.instance,
            ...(body ? { 'Content-Type': 'application/json' } : {}),
        },
        body: body ? JSON.stringify(body) : undefined,
    });
    if (!res.ok) throw new Error(`${method} ${path} failed with ${res.status}`);
    return (await res.json()) as T;
}

export const fetchWidget = (env: Ready) =>
    call<WidgetResponse>(env, 'GET', `/api/widget/${encodeURIComponent(env.compId)}`);

/**
 * Editor-only bookkeeping, run before the first fetch: a site copied from
 * another gets its Instance and content; a widget copied from another gets
 * that widget's content; any other widget is registered on the site.
 */
export async function registerWidget(env: Ready) {
    if (peekInstance(env.instance).originInstanceId) {
        await call(env, 'POST', '/api/instance/duplicate-instance', { compId: env.compId });
    }
    if (env.originCompId && env.originCompId !== env.compId) {
        await call(env, 'POST', '/api/instance/duplicate-widget', {
            compId: env.compId,
            origCompId: env.originCompId,
        });
    } else {
        await call(env, 'POST', '/api/instance/add-compid', { compId: env.compId });
    }
}
